<template lang="pug">
  .team-builder-define
    WtTitle(card-title v-if="previewMode") Step 3: Review Team Details
    WtTitle(card-title v-else) Step 1: Define Your Team
    .columns
      .column.is-4
        BField(label="Upload Team Logo")
          WtUploadImage(
            entity="team"
            @updateImage="image = $event"
          )
      .column.is-8
        .columns
          .column.is-half
            UniquenessCheck(
              label="Team Name"
              entity="teamName"
              placeholder="Team Name"
              v-model="name"
              @validName="$emit('disableNext', !$event)"
              :readonly="previewMode"
              :programId="_.get(registrationData, 'programId', 0)"
            )
          .column.is-half
            // INVITE ONLY
            WtRadioQuestionInput.block(
              label="Who can see and join this team?"
              :preview-mode="previewMode"
              :preview-value="inviteOnlyText"
            )
              template(v-slot:radioOne)
                BRadio.define-radio(
                  v-model="inviteOnly"
                  :native-value="0"
                )
                  | {{allMembersLabel}}
              template(v-slot:radioTwo)
                BRadio.define-radio(
                  v-model="inviteOnly"
                  :native-value="1"
                )
                  | Selected members only
        .columns(v-if="isAdmin && isProTier")
          .column.is-half(v-if="this.$auth.user().program.organizationsOn")
            .is-size-6.label Organization
            WtTile.is-align-items-center
              BDropdown(
                v-model="organizationId"
                :disabled="previewMode && !editOrganization"
              ).full-width-dropdown
                .sort-by-trigger(slot="trigger" slot-scope="{ active }")
                  BButton(
                    :label="organizationId ? _.find(programOrganizations, {id :organizationId}).name : 'Select Organization'"
                    :icon-right="active ? 'chevron-up' : 'chevron-down'"
                  )
                template(
                  v-for="option in programOrganizations"
                  v-if="option.active"
                )
                  BDropdownItem(
                    :value="option.id"
                  ) {{ option.name }}
              BIcon.preview-mode-icon.ml-2(
                v-if="previewMode && !editOrganization"
                @click.native="editOrganizationMode()"
                icon="settings"
              )
        BField(
          :type="{ 'is-danger': errors.has('description') }"
          :message="errors.first('description')"
          label="Team Description"
        )
          BInput#team-description(
            type="textarea"
            maxlength="1000"
            name="description"
            v-model="description"
            placeholder="Team description"
            v-validate="'max:1000'"
          )
        BField.card(v-if="previewMode")
          MembersTable(mode="receiver")

</template>

<script>
import WtTile from "@@/WtTile/WtTile";
import WtTitle from "@@/WtTitle/WtTitle";
import WtButton from "@@/WtButton/WtButton";
import WtUploadImage from "@@/WtUploadImage/WtUploadImage";
import UniquenessCheck from "@@/WtUniquenessCheckInput/WtUniquenessCheckInput";
import WtRadioQuestionInput from "@@/WtRadioQuestionInput/WtRadioQuestionInput";
import MembersTable from "@@/Members/MembersTable";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "TeamBuilderDefine",

  components: {
    WtTile,
    WtTitle,
    WtButton,
    WtUploadImage,
    UniquenessCheck,
    MembersTable,
    WtRadioQuestionInput
  },

  props: {
    formData: {
      type: FormData,
      required: true,
      validator: value => {
        return value instanceof FormData;
      }
    },

    previewMode: {
      type: Boolean,
      default: false
    },

    setSelectedOrganizationId: {
      type: Function,
      default: () => {}
    },

    registrationBuilder: {
      type: Boolean,
      default: false
    },

    registrationData: {
      type: Object,
      default: () => {}
    },

    organization: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      name: "",
      inviteOnly: 0,
      description: "",
      image: null,
      editOrganization: false,
      organizationId: null
    };
  },

  computed: {
    ...mapGetters("authMember", ["isAdmin"]),
    ...mapGetters("program", [
      "programOrganizations",
      "programOrganizationsLoaded",
      "isProTier",
      "coreProgramMeta"
    ]),
    ...mapGetters("members", ["member"]),
    ...mapGetters("registration", ["programRegistrationFields"]),

    inviteOnlyText() {
      return this.inviteOnly ? "Selected members only" : this.allMembersLabel;
    },
    activityBadges: {
      set(activityBadges) {
        this.updateProgramSettingsCopy({
          key: "activityBadgesOn",
          value: activityBadges ? 1 : 0
        });
      },
      get() {
        return Number(this.programSettingsCopy.activityBadgesOn);
      }
    },

    allMembersLabel() {
      if (
        this.programRegistrationFields.organizationsOn ||
        this.coreProgramMeta.organizationsOn ||
        this.organizationId ||
        this._.get(this.organization, "value", false)
      ) {
        return "Organization Members";
      }
      return "All members";
    }
  },

  watch: {
    name: {
      handler: function(newValue) {
        this.formData.set("name", newValue);
      },
      immediate: false
    },

    inviteOnly: {
      handler: function(newValue) {
        this.formData.set("inviteOnly", newValue);
      },
      immediate: false
    },

    description: {
      handler: function(newValue) {
        this.formData.set("description", newValue);
      },
      immediate: false
    },

    image: {
      handler: function(newValue) {
        if (newValue === null) {
          this.formData.delete("image");
        } else {
          this.formData.set("image", newValue);
        }
      },
      immediate: false
    },
    organizationId: {
      handler: function(newValue) {
        this.formData.set("organizationId", newValue);
        this.setSelectedOrganizationId(newValue);
        if (this.previewMode) {
          this.editOrganization = false;
        }
      },
      immediate: false
    }
  },
  mounted() {
    if (this.isProTier) {
      if (this.isAdmin && !this.programOrganizationsLoaded) {
        this.fetchProgramOrganizations({
          programId: this.$auth.user().programId
        });
      } else {
        this.organizationId = this._.get(this.member, "organizationId", null);
      }
    }
  },

  methods: {
    ...mapActions("program", ["fetchProgramOrganizations"]),

    editOrganizationMode() {
      this.editOrganization = true;
    }
  }
};
</script>

<style lang="sass" scoped>
.team-builder-define
  .members-table
    padding: 1rem

  .full-width-dropdown
    width: 100%

    /deep/ div, button
      width: 100%

    /deep/ button
      display: flex
      justify-content: space-between
</style>
